import { ArrowRight } from "@tamagui/lucide-icons";
import { useContext } from "react";

import {
  type GetOrCreateUserProgressionUserProgressionPostMutationBody,
  useGetOrCreateUserProgression,
  useGetStatusOfEncounterAssociatedWithCuraiVisit,
} from "@/api/generated";
import {
  type CuraiRedirectContent,
  DefaultVisitMetadataVisitTypePreference,
  EncounterStartType,
  ProductOption,
  VisitType,
} from "@/api/generated/models";
import { AssistantClientEvent } from "@/api/model/Conversation";
import { useUpdateCuraiVisit } from "@/api/mutations/clinic";
import { useIsChatMutationPending } from "@/api/mutations/conversation";
import { useGetClinicStatusForPatient } from "@/api/queries/clinicStatus";
import { useGetVisitOptionsForAFlow } from "@/api/queries/visitOptions";
import {
  Button,
  ChatNote,
  LayoutContext,
  Link,
  Logo,
  Theme,
  ThinkingAnimation,
} from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import ChatOrVideoNote from "@/conversation/AssistantMessage/ClinicRequest/CuraiRedirect/ChatOrVideoNote";
import OfferCard, {
  getOfferCopyText,
  useOfferVariation,
} from "@/conversation/AssistantMessage/ClinicRequest/OfferCard";
import VisitStatusCard from "@/conversation/AssistantMessage/ClinicRequest/VisitStatusCard";
import MessageSatisfaction from "@/conversation/MessageSatisfaction";
import { usePatientResponsibility } from "@/conversation/sidebar/Payment/usePatientResponsibility";
import UserProgressionFlow from "@/conversation/sidebar/UserProgression/UserProgressionFlow";
import { useAnalytics } from "@/hooks/analytics";
import { useCurrentConversationId } from "@/hooks/conversation";
import { usePartnerIntent } from "@/hooks/usePartnerIntent";
import {
  useHasTalkToDoctorAiUpsellStoreHydrated,
  useTalkToDoctorAiUpsellStore,
  useUseCaseStore,
} from "@/store";
import type { AssistantMessageTypes } from "@/types";

export interface CuraiRedirectProps extends AssistantClientEvent {
  content: CuraiRedirectContent;
  type: AssistantMessageTypes.CURAI_REDIRECT;
  /**
   * Undefined until the request starts a visit — deliberately not the CHAI conversation's
   * curai_conversation_id, which is deprecated
   */
  clinicConversationId?: string | null;
}

const FREE_CURAI_CARD_COPY =
  "Curai Health Medical Group is now offering free telehealth visits - patients only pay for labs or medications (if needed).";

export default function CuraiRedirect({
  createdAt,
  content,
  messageId,
  isLatest,
  feedback,
  feedbackComment,
  maybeCopy,
  clinicRequestId,
  originClinicRequestId,
  clinicConversationId,
  conversationId: eventConversationId,
}: CuraiRedirectProps) {
  const urlConversationId = useCurrentConversationId();
  // Everything below is scoped to the conversation that produced this card — its care plan, its
  // visit, the use-case and upsell decisions recorded against it. Under
  // `keep-conversations-open` the stream renders a whole group, so that is not the url's
  // conversation once a handoff has opened a newer one.
  const conversationId = eventConversationId ?? urlConversationId;
  const { setModalContent } = useContext(LayoutContext);
  const { value: usesPaymentProgression, loading: isPaywallFlagLoading } =
    useFeatureFlag("chai-paywall-user-progression", false);
  const { value: insurancePayEnabled, loading: isInsuranceFlagLoading } =
    useFeatureFlag("insurance-pay", false);
  const { value: visitsDisabled, loading: isVisitsFlagLoading } =
    useFeatureFlag("visits-disabled", false);
  const { value: liveChatDisabled } = useFeatureFlag(
    "disable-live-chat",
    false,
  );
  const {
    value: keepConversationsOpen,
    loading: isKeepConversationsOpenLoading,
  } = useFeatureFlag("keep-conversations-open", false);

  const conversationUseCase = useUseCaseStore((s) =>
    s.getConversationUseCase(conversationId),
  );
  const isTalkToDoctorConversation = conversationUseCase === "talk-to-a-doctor";
  const upsellDecision = useTalkToDoctorAiUpsellStore((s) =>
    s.getUpsellDecision(conversationId),
  );

  const hasUpsellStoreHydrated = useHasTalkToDoctorAiUpsellStoreHydrated();

  const { data: clinicStatus, isLoading: clinicStatusLoading } =
    useGetClinicStatusForPatient();
  const { data: visitOptionsData, isLoading: visitOptionsLoading } =
    useGetVisitOptionsForAFlow({
      clinicRequestId,
      enabled: usesPaymentProgression,
      flowName: "default_visit",
    });
  const { productOptions = [], visitOptions = [] } = visitOptionsData ?? {};
  const { offerVariation, partnerName } = useOfferVariation(visitOptions);
  const { data: encounterStatusData, isLoading: encounterStatusLoading } =
    useGetStatusOfEncounterAssociatedWithCuraiVisit(
      clinicConversationId as string,
      clinicRequestId ? { clinic_request_id: clinicRequestId } : undefined,
      {
        query: {
          enabled: !!clinicConversationId,
        },
      },
    );
  const { mutate: updateCuraiVisit, isPending: isUpdatingCuraiVisit } =
    useUpdateCuraiVisit(conversationId as string, messageId);

  const { mutateAsync: getOrCreateUserProgression } =
    useGetOrCreateUserProgression();
  const { getLocalPartnerIntent } = usePartnerIntent();

  const isPending = useIsChatMutationPending();
  const showSatisfaction = (isLatest && !isPending) || !isLatest;

  const { track } = useAnalytics();

  const ongoingEncounterType = clinicStatus?.latest_visit?.type;
  const ongoingSessionId =
    ongoingEncounterType === EncounterStartType.ASYNC_RX ||
    ongoingEncounterType === EncounterStartType.RX_RENEWAL ||
    ongoingEncounterType === EncounterStartType.ASYNC_DX
      ? clinicStatus?.clinic_request_id
      : clinicStatus?.conversation?.id;

  // If the ongoing visit is an ASYNC_RX, RX_RENEWAL, or ASYNC_DX, block updates
  const isAsync =
    ongoingEncounterType === EncounterStartType.ASYNC_RX ||
    ongoingEncounterType === EncounterStartType.RX_RENEWAL ||
    ongoingEncounterType === EncounterStartType.ASYNC_DX;

  const encounterStatus = encounterStatusData?.status;
  const ageInHours = (Date.now() - Date.parse(createdAt)) / 1000 / 60 / 60;

  const canResumeVisit =
    Boolean(clinicConversationId) &&
    (encounterStatus === "in-progress" || encounterStatus === "pending");
  const hasVisitEnded =
    encounterStatus === "incomplete" || encounterStatus === "completed";
  const showVisitStatusCard =
    Boolean(content?.archived) ||
    content.action_taken === "visit_updated" ||
    canResumeVisit ||
    hasVisitEnded;

  const showsExistingVisitCard =
    showVisitStatusCard ||
    (!content.action_taken &&
      clinicStatus?.hasOngoingClinicVisit &&
      Boolean(ongoingSessionId) &&
      ongoingSessionId !== clinicConversationId) ||
    (!clinicConversationId && ageInHours > 600) ||
    visitsDisabled;

  const showOfferCard =
    !showsExistingVisitCard &&
    usesPaymentProgression &&
    visitOptions.length > 0;

  const { isLoading: isCheckingInsurance } = usePatientResponsibility({
    enabled:
      insurancePayEnabled &&
      !clinicStatusLoading &&
      !encounterStatusLoading &&
      showOfferCard &&
      offerVariation === "cash_pay",
  });

  if (
    isTalkToDoctorConversation &&
    (!hasUpsellStoreHydrated ||
      upsellDecision === undefined ||
      (upsellDecision.kind === "accepted" &&
        upsellDecision.messageId === messageId))
  ) {
    return null;
  }

  // Loading state for insurance check is different from other loading states.
  // These flags determine which loading state to use, so to avoid a flicker render nothing until we know.
  if (visitOptionsLoading || isPaywallFlagLoading || isInsuranceFlagLoading) {
    return null;
  }

  // The insurance check *should* take longer than the other queries
  // And if it's faster than them, the ThinkingAnimation isn't immediately visible anyway so still no flicker
  if (isCheckingInsurance) {
    return <ThinkingAnimation isVisible />;
  }

  // Everything below this point depends on clinic / encounter / careplan
  // queries — show a skeleton while they resolve.
  if (
    clinicStatusLoading ||
    encounterStatusLoading ||
    isPaywallFlagLoading ||
    isVisitsFlagLoading ||
    isKeepConversationsOpenLoading
  ) {
    return <ChatNote.Skeleton />;
  }

  if (showVisitStatusCard) {
    return (
      <VisitStatusCard
        content={content}
        conversationId={conversationId}
        clinicRequestId={clinicRequestId}
        clinicConversationId={clinicConversationId}
        messageId={messageId}
        feedback={feedback}
        feedbackComment={feedbackComment}
        maybeCopy={maybeCopy}
        isLatest={isLatest}
      />
    );
  }

  // Next highest priority render - if card hasn't been actioned yet and a
  // visit is already started, allow for updating the ongoing visit
  if (
    !content.action_taken &&
    clinicStatus?.hasOngoingClinicVisit &&
    ongoingSessionId &&
    ongoingSessionId !== clinicConversationId &&
    isAsync
  ) {
    const copyableText =
      "Your request is being reviewed\n\nYour clinician is reviewing your existing request. Once it's completed, you can start a new visit here.";
    return (
      <>
        <Theme name="clinic">
          <ChatNote.Active
            Icon={Logo.Only}
            title="Your request is being reviewed"
            subtitle="Your clinician is reviewing your existing request. Once it's completed, you can start a new visit here."
            button={
              <Button mode="primary" disabled={true}>
                Request in progress
              </Button>
            }
          />
        </Theme>
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy(copyableText)}
          />
        )}
      </>
    );
  }

  if (
    !content.action_taken &&
    clinicStatus?.hasOngoingClinicVisit &&
    ongoingSessionId &&
    ongoingSessionId !== clinicConversationId
  ) {
    const copyableText =
      "You're already in a visit with a clinician — we'll share this new concern with them.\n\nOne visit, all your concerns\n\nWe'll share this update with your Curai Health clinician so they have the full picture and can help you.";
    return (
      <>
        <Theme name="clinic">
          <ChatNote.Active
            Icon={Logo.Only}
            title="One visit, all your concerns"
            subtitle="We'll share this update with your Curai Health clinician so they have the full picture and can help you."
            button={
              <Button
                mode="primary"
                iconAfter={ArrowRight}
                disabled={isUpdatingCuraiVisit}
                onPress={() =>
                  updateCuraiVisit({
                    data: {
                      conversation_id: conversationId as string,
                      clinic_request_id: clinicRequestId as string,
                    },
                  })
                }
              >
                Update my doctor
              </Button>
            }
          />
        </Theme>
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy(copyableText)}
          />
        )}
      </>
    );
  }

  const handleStartVisit = async (
    preference?: DefaultVisitMetadataVisitTypePreference,
    chosenProductOption?: ProductOption,
  ) => {
    const resolvedPreference =
      chosenProductOption === ProductOption.live_chat
        ? DefaultVisitMetadataVisitTypePreference.chat
        : chosenProductOption === ProductOption.live_video
          ? DefaultVisitMetadataVisitTypePreference.audiovisual
          : (preference ?? DefaultVisitMetadataVisitTypePreference.audiovisual);
    const isChat =
      resolvedPreference === DefaultVisitMetadataVisitTypePreference.chat;
    const isSubscription =
      chosenProductOption === ProductOption.all_you_can_eat_subscription;
    track(AD_CONVERSION_EVENTS.startAVisit, {
      conversation_id: conversationId,
      clinic_request_id: clinicRequestId,
      ...(chosenProductOption && { product_option: chosenProductOption }),
      ...(!isSubscription && {
        encounter_type: isChat
          ? EncounterStartType.TEXT_BASED
          : EncounterStartType.AUDIOVISUAL,
        visit_type: isChat ? VisitType.live_chat : VisitType.live_video,
      }),
    });
    const partnerIntent = await getLocalPartnerIntent();
    const progressionRequest: GetOrCreateUserProgressionUserProgressionPostMutationBody =
      {
        flow_name: "default_visit",
        // Scopes the progression to this visit request, so a second one gets its own.
        clinic_request_id: clinicRequestId,
        conversation_id: conversationId,
        metadata: {
          visit_type_preference: resolvedPreference,
          // If no product is chosen, we provide an explicit null which clears a stale product.
          // This happens in the case there are multiple modalities, and we must show visit options
          payment_product_option: chosenProductOption ?? null,
          ...(partnerIntent && { partner_intent: partnerIntent }),
        },
      };
    const userProgression = await getOrCreateUserProgression({
      data: progressionRequest,
    });
    setModalContent(() => (props: any) => (
      <UserProgressionFlow
        initialProgression={userProgression}
        originClinicRequestId={originClinicRequestId}
        {...props}
      />
    ));
  };

  // Next highest priority render - if visit is not started, but the link
  // was created more than 6 hours ago, reflect into UI and ask the user for
  // what they need based off of flag
  if (!clinicConversationId && ageInHours > 600) {
    const title = "Still want to talk to a clinician?";
    const message = keepConversationsOpen
      ? "Your previous link timed out. Tell me what you need and I can set up a new visit."
      : "Your previous link timed out. To continue, you'll need to start a new chat.";

    const subtitle = keepConversationsOpen ? (
      message
    ) : (
      <>
        Your previous link timed out. To continue, you'll need to{" "}
        <Link size="$button" href="/">
          start a new chat
        </Link>
        .
      </>
    );

    return (
      <>
        <ChatNote Icon={Logo.Only} title={title} subtitle={subtitle} />
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy(`${title}\n\n${message}`)}
          />
        )}
      </>
    );
  }

  // If visits are turned off, don't offer to start a new visit — show that the
  // clinic is currently unavailable. Existing/ongoing visits are handled above.
  if (visitsDisabled) {
    const copyableText =
      "Clinic currently unavailable\n\nOur clinic isn't accepting new visits right now. Please check back soon.";
    return (
      <>
        <Theme name="clinic">
          <ChatNote.Active
            Icon={Logo.Only}
            title="Clinic currently unavailable"
            subtitle="Our clinic isn't accepting new visits right now. Please check back soon."
            button={
              <Button mode="primary" disabled={true}>
                Unavailable
              </Button>
            }
          />
        </Theme>
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy(copyableText)}
          />
        )}
      </>
    );
  }

  return (
    <>
      {showOfferCard ? (
        <OfferCard
          offerVariation={offerVariation}
          partnerName={partnerName}
          productOptions={productOptions}
          onStartVisit={(chosenProductOption) =>
            handleStartVisit(undefined, chosenProductOption)
          }
        />
      ) : (
        <ChatOrVideoNote
          description={FREE_CURAI_CARD_COPY}
          onVideoPress={() =>
            handleStartVisit(
              DefaultVisitMetadataVisitTypePreference.audiovisual,
            )
          }
          onChatPress={
            liveChatDisabled
              ? undefined
              : () =>
                  handleStartVisit(DefaultVisitMetadataVisitTypePreference.chat)
          }
        />
      )}
      {showSatisfaction && (
        <MessageSatisfaction
          messageId={messageId}
          feedback={feedback}
          feedbackComment={feedbackComment}
          onCopy={() =>
            maybeCopy(showOfferCard ? getOfferCopyText() : FREE_CURAI_CARD_COPY)
          }
        />
      )}
    </>
  );
}
